import { inject, Injectable } from '@angular/core';
import { FirestoreAdapter } from '@shared/firestore/firestore-adapter';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import {
  IGrantedOffer,
  IGrantedOfferWithOffering,
} from '@models/IGrantedOffer';
import { IOffering } from '../ai-concierge/models/IOffering';
import { SharedService } from './shared.service';
import { SessionService } from './session.service';
import { CompanyStore } from '@shared/store/company';
import { LocationStore } from '@shared/store/locations';
import { toObservable } from '@angular/core/rxjs-interop';

/** Granted offer with customerId from path (when listing by location). */
export interface IGrantedOfferWithCustomer extends IGrantedOfferWithOffering {
  customerId: string;
}

@Injectable({
  providedIn: 'root',
})
export class GrantedOffersService {
  private firestore = inject(FirestoreAdapter);
  private sharedService = inject(SharedService);
  private sessionService = inject(SessionService);
  #companyStore = inject(CompanyStore);
  #locationStore = inject(LocationStore);

  /** Emits the current company's location ids once they have loaded. */
  #companyLocations$ = toObservable(this.#locationStore.locations);

  /**
   * Get a single granted offer by customer and grant id.
   */
  getGrantedOffer(
    customerId: string,
    grantId: string,
  ): Observable<IGrantedOfferWithOffering | null> {
    if (!customerId || !grantId) return of(null);
    const docRef = this.firestore
      .collection('customers')
      .doc(customerId)
      .collection<IGrantedOffer>('grantedOffers')
      .doc(grantId);
    return docRef.get().pipe(
      map((snap) => {
        if (!snap.exists) return null;
        return { id: snap.id, ...snap.data() } as IGrantedOffer;
      }),
      switchMap((grant) => {
        if (!grant) return of(null);
        if (this.hasDenormalized(grant)) {
          const offering = this.synthesizeOfferingFromGrant(grant);
          return of({
            ...grant,
            offering,
            offeringName: grant.offeringName,
          } as IGrantedOfferWithOffering);
        }
        return this.getOfferingDetails(grant).pipe(
          map((offering) => ({
            ...grant,
            offering: offering ?? undefined,
            offeringName: offering?.name,
          })),
        );
      }),
      catchError((err) => {
        this.sharedService.logErrors(
          err,
          'loading granted offer',
          'GrantedOffersService',
        );
        return of(null);
      }),
    );
  }

  /**
   * Returns the set of customer IDs that have at least one document in their
   * grantedOffers subcollection (customers/{customerId}/grantedOffers).
   */
  getCustomerIdsWithOffers(): Observable<Set<string>> {
    const allowed = this.getAllowedLocationIds();
    return this.firestore
      .collectionGroup('grantedOffers')
      .get()
      .pipe(
        map((snap) => {
          const ids = new Set<string>();
          snap.docs.forEach((d) => {
            const path = d.ref.path;
            const parts = path.split('/');
            if (
              parts[0] === 'customers' &&
              parts[2] === 'grantedOffers' &&
              parts.length >= 4
            ) {
              if (allowed) {
                const data = d.data() as Record<string, unknown>;
                const locId = this.normalizeLocationIdFromGrant(
                  data as unknown as IGrantedOffer,
                );
                if (!locId || !allowed.has(locId)) return;
              }
              ids.add(parts[1]);
            }
          });
          return ids;
        }),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'fetching customer IDs with offers',
            'GrantedOffersService',
          );
          return of(new Set<string>());
        }),
      );
  }

  /**
   * Returns the set of allowed location IDs for the current session.
   */
  private getAllowedLocationIds(): Set<string> | null {
    const ids = this.sessionService.locationIds();
    if (!ids?.length) return null;
    return new Set(ids);
  }

  /**
   * Filter grants to only those whose locationId belongs to the current session.
   */
  private filterBySessionLocations<T extends IGrantedOffer>(grants: T[]): T[] {
    const allowed = this.getAllowedLocationIds();

    if (!allowed) return grants;
    return grants.filter((g) => {
      const locId = this.normalizeLocationIdFromGrant(g);
      return locId != null && allowed.has(locId);
    });
  }

  /**
   * Normalize locationId from a grant doc (DocumentReference or string) to string id.
   */
  private normalizeLocationIdFromGrant(
    grant: IGrantedOffer & { customerId?: string },
  ): string | null {
    const loc = grant.locationId;
    if (!loc) return null;
    if (typeof loc === 'string') return loc;
    const ref = loc as { id?: string; path?: string };
    if (ref.id) return ref.id;
    if (typeof ref.path === 'string') return ref.path.split('/').pop() ?? null;
    return null;
  }

  /**
   * List ALL granted offers (including validated and expired) for reporting.
   *
   * Scoping (prevents cross-company/location leakage):
   * 1. Wait for the current company's locations to load (they are queried
   *    `where companyId == companyRef`, so they are inherently company-scoped).
   * 2. Bound grants to that location set, intersected with the user's own
   *    locations when the user is a Manager. Admins (no per-location list) see
   *    every location in their company, but never other companies.
   * 3. Keep the denormalized `companyId` check as a secondary guard for grants
   *    that already carry it (post-backfill).
   */
  listAllOffersForReport(): Observable<IGrantedOfferWithCustomer[]> {
    return this.#companyLocations$.pipe(
      filter((locations) => locations.length > 0),
      take(1),
      map((locations) => this.allowedReportLocationIds(locations)),
      switchMap((allowed) => {
        const companyRef = this.firestore.doc(
          `companies/${this.#companyStore.company().id}`,
        ).ref;
        const resolve = (grants: (IGrantedOffer & { customerId: string })[]) =>
          this.resolveOfferingsWithCustomerId(grants);

        // Prefer the server-side companyId filter to avoid reading every
        // grantedOffers doc globally. This requires a denormalized `companyId`
        // field on grant docs AND a collectionGroup composite index. When the
        // field/index is missing the query errors, so we fall back to the
        // original read-all + in-memory filter. Add the index + backfill to
        // make the filtered path the norm and drop the fallback.
        return this.firestore
          .collectionGroup('grantedOffers', (ref) =>
            ref.where('companyId', '==', companyRef),
          )
          .get()
          .pipe(
            map((snap) => this.buildReportGrants(snap, allowed)),
            switchMap(resolve),
            catchError((err) => {
              console.warn(
                'companyId-filtered grantedOffers query failed; falling back to unfiltered read',
                err,
              );
              return this.firestore
                .collectionGroup('grantedOffers')
                .get()
                .pipe(
                  map((snap) => this.buildReportGrants(snap, allowed)),
                  switchMap(resolve),
                );
            }),
          );
      }),
      catchError((err) => {
        console.warn('Failed to list granted offers for report', err);
        return of([]);
      }),
    );
  }

  /**
   * Map a grantedOffers collectionGroup snapshot to report grants, applying the
   * company + location in-memory guards (secondary safety net behind the
   * server-side companyId filter).
   */
  private buildReportGrants(
    snap: unknown,
    allowed: Set<string>,
  ): (IGrantedOffer & { customerId: string })[] {
    const docs = (
      snap as {
        docs: Array<{
          ref: { path: string };
          data: () => Record<string, unknown>;
          id: string;
        }>;
      }
    ).docs;
    return docs
      .map((d) => {
        const parts = d.ref.path.split('/');
        const data = (d.data() || {}) as Partial<IGrantedOffer>;
        const customerId =
          parts[0] === 'customers' &&
          parts[2] === 'grantedOffers' &&
          parts.length >= 4
            ? parts[1]
            : '';
        if (!customerId) return null;
        const companyId = data.companyId?.id;
        // Secondary guard: drop grants explicitly tagged to another company.
        if (companyId && companyId !== this.#companyStore.company().id) {
          return null;
        }
        // Primary guard: grant's location must belong to the company
        // (and the Manager's own locations) regardless of companyId.
        const grant = {
          id: d.id,
          ...data,
          customerId,
        } as IGrantedOffer & { customerId: string };
        const locId = this.normalizeLocationIdFromGrant(grant);
        if (!locId || !allowed.has(locId)) return null;
        return grant;
      })
      .filter((x): x is IGrantedOffer & { customerId: string } => x != null);
  }


  /**
   * Build the allowed location-id set for the report: the company's locations,
   * intersected with the Manager's own locations when applicable.
   */
  private allowedReportLocationIds(
    companyLocations: { id?: string }[],
  ): Set<string> {
    const companyIds = new Set(
      companyLocations
        .map((l) => l.id)
        .filter((id): id is string => id != null && id !== ''),
    );
    const userIds = this.sessionService.locationIds();
    if (this.sessionService.isManager() && userIds.length) {
      return new Set(userIds.filter((id) => companyIds.has(id)));
    }
    return companyIds;
  }

  /**
   * List all redeemable granted offers (non-validated, non-expired).
   * Used when "All locations" is selected. No where clause to avoid requiring a Firestore index;
   * we filter validated and expired in memory.
   */
  listAllOffers(): Observable<IGrantedOfferWithCustomer[]> {
    return this.firestore
      .collectionGroup('grantedOffers')
      .get()
      .pipe(
        map((snap) =>
          this.mapSnapToGrantsWithCustomer(
            snap as {
              docs: Array<{
                ref: { path: string };
                data: () => Record<string, unknown>;
                id: string;
              }>;
            },
          ),
        ),
        map((grants) => this.filterBySessionLocations(grants)),
        map((grants) => grants.filter((g) => g.validated !== true)),
        switchMap((grants) => this.resolveOfferingsWithCustomerId(grants)),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'listing all granted offers',
            'GrantedOffersService',
          );
          return of([]);
        }),
      );
  }

  private mapSnapToGrantsWithCustomer(snap: {
    docs: Array<{
      ref: { path: string };
      data: () => Record<string, unknown>;
      id: string;
    }>;
  }): (IGrantedOffer & { customerId: string })[] {
    const list: (IGrantedOffer & { customerId: string })[] = snap.docs
      .map((d) => {
        const path = d.ref.path;
        const parts = path.split('/');
        const customerId =
          parts[0] === 'customers' &&
          parts[2] === 'grantedOffers' &&
          parts.length >= 4
            ? parts[1]
            : '';
        if (!customerId) return null;
        const data = d.data() ?? {};
        return {
          id: d.id,
          ...data,
          customerId,
        } as IGrantedOffer & { customerId: string };
      })
      .filter((x): x is IGrantedOffer & { customerId: string } => x != null);
    const now = new Date().toISOString();
    return list.filter((g) => {
      if (g.expiresOn && g.expiresOn < now) return false;
      return true;
    });
  }

  /**
   * List granted offers for a location (non-validated, non-expired).
   * Tries indexed query first; on failure falls back to listAllOffers + filter in memory.
   */
  listByLocation(locationId: string): Observable<IGrantedOfferWithCustomer[]> {
    if (!locationId) return of([]);
    const locationRef = this.firestore.doc(`locations/${locationId}`).ref;
    return this.firestore
      .collectionGroup('grantedOffers', (ref) =>
        ref
          .where('locationId', '==', locationRef)
          .where('validated', '==', false),
      )
      .get()
      .pipe(
        map((snap) =>
          this.mapSnapToGrantsWithCustomer(
            snap as {
              docs: Array<{
                ref: { path: string };
                data: () => Record<string, unknown>;
                id: string;
              }>;
            },
          ),
        ),
        switchMap((grants) => this.resolveOfferingsWithCustomerId(grants)),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'listing granted offers by location (indexed)',
            'GrantedOffersService',
          );
          return this.listAllOffers().pipe(
            map((all) =>
              all.filter(
                (g) => this.normalizeLocationIdFromGrant(g) === locationId,
              ),
            ),
          );
        }),
      );
  }

  private resolveOfferingsWithCustomerId(
    grants: (IGrantedOffer & { customerId: string })[],
  ): Observable<IGrantedOfferWithCustomer[]> {
    if (grants.length === 0) return of([]);
    const withOfferings$ = grants.map((grant) => {
      if (this.hasDenormalized(grant)) {
        const offering = this.synthesizeOfferingFromGrant(grant);
        return of({
          ...grant,
          offering,
          offeringName: grant.offeringName,
          customerId: grant.customerId,
        } as IGrantedOfferWithCustomer);
      }
      return this.getOfferingDetails(grant).pipe(
        map(
          (offering) =>
            ({
              ...grant,
              offering: offering ?? undefined,
              offeringName: offering?.name,
              customerId: grant.customerId,
            }) as IGrantedOfferWithCustomer,
        ),
      );
    });
    return forkJoin(withOfferings$);
  }

  /**
   * Get active (non-validated, non-expired) granted offers for a customer.
   * Resolves offering name from locations/{locationId}/offerings/{offeringId}.
   */
  getGrantedOffersByCustomer(
    customerId: string,
  ): Observable<IGrantedOfferWithOffering[]> {
    if (!customerId) return of([]);

    return this.firestore
      .collection('customers')
      .doc(customerId)
      .collection<IGrantedOffer>('grantedOffers', (ref) =>
        ref.where('validated', '==', false),
      )
      .get()
      .pipe(
        map((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as IGrantedOffer),
        ),
        switchMap((grants) => this.resolveOfferings(grants)),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'loading granted offers',
            'GrantedOffersService',
          );
          return of([]);
        }),
      );
  }

  /**
   * Full granted-offer history for a customer (includes validated + expired).
   * Used by the customer activity log so post-validation entries remain visible.
   */
  getAllGrantedOffersByCustomer(
    customerId: string,
  ): Observable<IGrantedOfferWithOffering[]> {
    if (!customerId) return of([]);

    return this.firestore
      .collection('customers')
      .doc(customerId)
      .collection<IGrantedOffer>('grantedOffers')
      .get()
      .pipe(
        map((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as IGrantedOffer),
        ),
        switchMap((grants) => this.resolveAllOfferings(grants)),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'loading all granted offers',
            'GrantedOffersService',
          );
          return of([]);
        }),
      );
  }

  /**
   * Like `resolveOfferings` but does NOT drop expired/validated grants.
   * Used by the activity log to render the full grant lifecycle.
   */
  private resolveAllOfferings(
    grants: IGrantedOffer[],
  ): Observable<IGrantedOfferWithOffering[]> {
    if (grants.length === 0) return of([]);
    const withOfferings$ = grants.map((grant) => {
      if (this.hasDenormalized(grant)) {
        const offering = this.synthesizeOfferingFromGrant(grant);
        return of({
          ...grant,
          offering,
          offeringName: grant.offeringName,
        } as IGrantedOfferWithOffering);
      }
      return this.getOfferingDetails(grant).pipe(
        map(
          (offering) =>
            ({
              ...grant,
              offering: offering ?? undefined,
              offeringName: offering?.name,
            }) as IGrantedOfferWithOffering,
        ),
      );
    });
    return forkJoin(withOfferings$);
  }

  /**
   * Filter out expired offers and resolve offering name for each grant.
   */
  private resolveOfferings(
    grants: IGrantedOffer[],
  ): Observable<IGrantedOfferWithOffering[]> {
    const now = new Date().toISOString();
    const active = grants.filter((g) => {
      if (!g.expiresOn) return true;
      return g.expiresOn > now;
    });

    if (active.length === 0) {
      return of([]);
    }

    const withOfferings$ = active.map((grant) => {
      if (this.hasDenormalized(grant)) {
        const offering = this.synthesizeOfferingFromGrant(grant);
        return of({
          ...grant,
          offering,
          offeringName: grant.offeringName,
        } as IGrantedOfferWithOffering);
      }
      return this.getOfferingDetails(grant).pipe(
        map(
          (offering) =>
            ({
              ...grant,
              offering: offering ?? undefined,
              offeringName: offering?.name,
            }) as IGrantedOfferWithOffering,
        ),
      );
    });

    return withOfferings$.length ? forkJoin(withOfferings$) : of([]);
  }

  /** Grant docs are denormalized when at least `offeringName` is present. */
  private hasDenormalized(grant: IGrantedOffer): boolean {
    return grant.offeringName != null;
  }

  /**
   * Build a minimal `IOffering`-shaped object from denormalized grant fields so
   * downstream consumers can read `offering.type` / `offering.amount` / `offering.name`
   * without an extra Firestore read. Only the two-type model fields are populated.
   */
  private synthesizeOfferingFromGrant(grant: IGrantedOffer): IOffering {
    const type: IOffering['type'] =
      grant.offeringType === 'store_credit' ? 'store_credit' : 'offer';
    const offering: IOffering = {
      name: grant.offeringName ?? 'Offer',
      type,
    };
    if (
      type === 'store_credit' &&
      grant.grantedDiscount != null &&
      Number(grant.grantedDiscount) > 0
    ) {
      offering.amount = Number(grant.grantedDiscount);
    }
    return offering;
  }

  /**
   * Fetch full offering document for a grant (name, type, amount).
   */
  private getOfferingDetails(
    grant: IGrantedOffer,
  ): Observable<IOffering | null> {
    const locationId =
      typeof grant.locationId === 'object' && grant.locationId?.id != null
        ? (grant.locationId as { id: string }).id
        : null;
    if (!locationId || !grant.offeringId) return of(null);

    const offeringDoc = this.firestore
      .collection('locations')
      .doc(locationId)
      .collection<IOffering>('offerings')
      .doc(grant.offeringId);

    return offeringDoc.get().pipe(
      map((snap) =>
        snap.exists ? ({ id: snap.id, ...snap.data() } as IOffering) : null,
      ),
      catchError(() => of(null)),
    );
  }
}
